import { useState } from 'react';
import { ChevronDown, ChevronUp, Crown, Shield, Mic2, FileText, Sliders, Info, Eye } from 'lucide-react';

/**
 * Compact, collapsible legend explaining what each role can do.
 * Rendered inside AdminPanel above the users table.
 */
const ROLES_INFO = [
  {
    key: 'dono',
    label: 'Dono',
    icon: Crown,
    color: 'amber',
    summary: 'Acesso total e exclusivo.',
    can: [
      'Tudo que o Administrador pode',
      'Acessar Financeiro (Dashboard, Previsão, Histórico)',
      'Acessar Orçamentos (solicitações da landing pública)',
      'Promover/rebaixar administradores',
    ],
    cant: [],
  },
  {
    key: 'administrador',
    label: 'Administrador',
    icon: Shield,
    color: 'purple',
    summary: 'Gerencia projetos, usuários e pagamentos.',
    can: [
      'Criar, editar e excluir projetos e personagens',
      'Aprovar e gerenciar usuários (exceto dono)',
      'Lançar e editar pagamentos (página Pagamentos)',
      'Enviar avisos, ver log de atividades, gerenciar ferramentas',
      'Acessar a Comunidade com dados sensíveis (PIX, etc.)',
    ],
    cant: [
      'Acessar Financeiro (dashboard + previsão)',
      'Acessar Orçamentos da landing',
      'Promover-se a dono',
    ],
  },
  {
    key: 'dublador',
    label: 'Dublador',
    icon: Mic2,
    color: 'teal',
    summary: 'Vê e entrega seus personagens atribuídos.',
    can: [
      'Ver dashboard com projetos onde está atribuído',
      'Marcar suas falas como entregues',
      'Solicitar substituição em um personagem',
      'Enviar arquivos de áudio para os projetos',
      'Editar próprio perfil (incluindo "Dubla IA")',
    ],
    cant: [
      'Ver dados de outros dubladores',
      'Acessar Pagamentos, Financeiro ou Admin',
      'Editar/criar projetos',
    ],
  },
  {
    key: 'roteirista',
    label: 'Roteirista',
    icon: FileText,
    color: 'blue',
    summary: 'Equipe de produção — recebe valor por minuto.',
    can: [
      'Ver projetos onde está atribuído como roteirista',
      'Receber pagamento automático calculado por duração',
      'Editar próprio perfil',
    ],
    cant: [
      'Acessar Admin, Financeiro ou Orçamentos',
      'Modificar dados de outros usuários',
    ],
  },
  {
    key: 'mixador',
    label: 'Mixador',
    icon: Sliders,
    color: 'pink',
    summary: 'Equipe de produção — recebe valor por minuto.',
    can: [
      'Ver projetos onde está atribuído como mixador',
      'Receber pagamento automático calculado por duração',
      'Baixar arquivos enviados pelos dubladores',
      'Editar próprio perfil',
    ],
    cant: [
      'Acessar Admin, Financeiro ou Orçamentos',
      'Modificar dados de outros usuários',
    ],
  },
  {
    key: 'visitante',
    label: 'Visitante',
    icon: Eye,
    color: 'indigo',
    summary: 'Acesso somente leitura + Ferramentas essenciais.',
    can: [
      'Ver Dashboard, Projetos, Retakes, Tutoriais, Histórico',
      'Ver Comunidade completa (com dados sensíveis)',
      'Usar Ferramentas: Criador de Roteiro e Analisador',
      'Ver os próprios pagamentos em modo leitura',
    ],
    cant: [
      'Editar/criar projetos, pagamentos ou avisos',
      'Acessar Admin, Financeiro, Orçamentos ou Log',
      'Ver as outras ferramentas (Calculadora, Organização)',
    ],
  },
];

const colorMap = {
  amber:  'border-amber-500/40 bg-amber-500/5 text-amber-300',
  purple: 'border-purple-500/40 bg-purple-500/5 text-purple-300',
  teal:   'border-teal-500/40 bg-teal-500/5 text-teal-300',
  blue:   'border-blue-500/40 bg-blue-500/5 text-blue-300',
  pink:   'border-pink-500/40 bg-pink-500/5 text-pink-300',
  indigo: 'border-indigo-500/40 bg-indigo-500/5 text-indigo-300',
};

const RolesLegend = () => {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState('dono');

  const current = ROLES_INFO.find(r => r.key === active) || ROLES_INFO[0];
  const ActiveIcon = current.icon;

  return (
    <div className="bg-slate-800/40 border border-slate-700 rounded-xl overflow-hidden" data-testid="roles-legend">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-5 py-3 text-left hover:bg-slate-800/60 transition-colors"
        data-testid="roles-legend-toggle"
      >
        <div className="flex items-center gap-2">
          <Info className="h-4 w-4 text-teal-400" />
          <span className="font-medium text-white">O que cada cargo pode fazer?</span>
          <span className="hidden sm:inline text-xs text-slate-500">Clique para expandir antes de promover alguém</span>
        </div>
        {open ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
      </button>

      {open && (
        <div className="border-t border-slate-700 p-5 space-y-4">
          {/* Tabs */}
          <div className="flex flex-wrap gap-2" data-testid="roles-legend-tabs">
            {ROLES_INFO.map(r => {
              const Icon = r.icon;
              const isActive = r.key === active;
              return (
                <button
                  key={r.key}
                  type="button"
                  onClick={() => setActive(r.key)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                    isActive
                      ? colorMap[r.color]
                      : 'bg-slate-800/50 text-slate-400 border-slate-700 hover:border-slate-500'
                  }`}
                  data-testid={`roles-legend-tab-${r.key}`}
                >
                  <Icon className="h-3.5 w-3.5" />
                  {r.label}
                </button>
              );
            })}
          </div>

          {/* Active role detail */}
          <div className={`rounded-lg border p-4 ${colorMap[current.color]}`}>
            <div className="flex items-center gap-2 mb-2">
              <ActiveIcon className="h-5 w-5" />
              <h3 className="font-semibold text-white">{current.label}</h3>
            </div>
            <p className="text-sm text-slate-300 mb-4">{current.summary}</p>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <p className="text-xs uppercase tracking-wider text-emerald-400 font-semibold mb-2">Pode</p>
                <ul className="space-y-1.5">
                  {current.can.map((item, idx) => (
                    <li key={`can-${idx}`} className="text-xs text-slate-200 flex items-start gap-2">
                      <span className="text-emerald-400 mt-0.5 shrink-0">✓</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {current.cant.length > 0 && (
                <div>
                  <p className="text-xs uppercase tracking-wider text-red-400 font-semibold mb-2">Não pode</p>
                  <ul className="space-y-1.5">
                    {current.cant.map((item, idx) => (
                      <li key={`cant-${idx}`} className="text-xs text-slate-400 flex items-start gap-2">
                        <span className="text-red-400 mt-0.5 shrink-0">✕</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RolesLegend;
